
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Grid, Cell, TextField, DataTable, TableBody, TableRow, TableColumn } from "react-md";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import QueryBuilder from "components/Homeowner/Dashboard/QueryBuilder";
import MDialog, { ACTION_APPLY, ACTION_CLOSE, ACTION_CLONE } from "components/Shared/MDialog.tsx";
import { MSelect } from "components/Shared";
import { move, reorder } from "components/Homeowner/Dashboard/reorder";
import QuickListItem from "components/Homeowner/Dashboard/QuickListItem";
import { id2List, getListStyle, controlClassnames, controlElements, translations, operators } from "components/Homeowner/Dashboard/QuickListConfig";
import { SIMPLE_QUICK_LIST_EDIT } from "typeConstants/rights";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";

class QuickListEdit extends Component {
  constructor(props) {
    super(props);
    this.getList = this.getList.bind(this);
    this.setList = this.setList.bind(this);
    this.getActions = this.getActions.bind(this);
    this.loadQuickList = this.loadQuickList.bind(this);
    this.loadTemplate = this.loadTemplate.bind(this);
    this.onValidateRules = this.onValidateRules.bind(this);

    this.onChange = this.onChange.bind(this);
    this.getOperators = this.getOperators.bind(this);
    this.onChangeTemplate = this.onChangeTemplate.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);

    this.getUpdateModel = this.getUpdateModel.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onClone = this.onClone.bind(this);
    this.fixQuery = this.fixQuery.bind(this);
    this.changeQuickListMode = this.changeQuickListMode.bind(this);
    
    this.state = {
      quickList: {},
      query: {},
      operators: [],
      queryFields: [],
      unselectedList: [],
      selectedList: [],
      hasChanges: false,
      templateCode: "",
      quickListNameError: (props.item.name || "").length > 48,
      activeTemplate: "AppraisalCases",
      simpleMode: props.defaultToSimpleMode,
      name: "",
      description: "",
      group: null,
    };
  }

  UNSAFE_componentWillMount() {
    this.loadQuickList(this.props.item);
  }

  changeQuickListMode() {
    if (!this.state.simpleMode) return;
    this.setState({ simpleMode: false });
  }

  loadQuickList(quickList) {
    let createMode = quickList.createMode === true;

    if (createMode) {
      // Create a fresh new blank QuickList
      quickList.quickListCode = "Created";
      quickList.template = this.props.templateList[0];
      quickList.columnKeys = "";
      quickList.id = 0;
      quickList.name = "";
      quickList.description = "";
      quickList.templateCode = quickList.template.templateCode;
      quickList.priority = 6; // QuickListPriority.User
      quickList.sortOrder = 100;
      quickList.filterCode = "";
    }
    this.loadTemplate(quickList.template, quickList, createMode);
  }

  // Load the Template or switch templates
  // quickList is null on a template switch
  loadTemplate(template, quickList, createMode) {
    this.setState({ activeTemplate: template.templateCode });
    // example field object { name: "firstName", operators: "all", label: "First Name", input: { type: "text" } },
    let queryFields = template.filterFields.map((c) => {
      // operators are an array of operators from the list of All Operators.
      // need to translate the single quotes to double quotes.
      return {
        value: c.key,
        operators: c.operators.includes("[") ? JSON.parse(c.operators.replace(/'+/g, "\"")) : c.operators,
        label: c.name,
        id: c.key,
        inputType: c.inputType
      };
    });

    let myCols = [];
    let otherCols = [];

    let originalSelectedCols = createMode || quickList.columnKeys === ""
      ? template.defaultColumnKeys
      : quickList.columnKeys.split(",");

    // populate the selected columns preserving the order of the columns
    originalSelectedCols.forEach((key) => {
      let foundCol = template.columns.find(c => c.key.toLowerCase() === key.toLowerCase());
      if (foundCol) { myCols.push(foundCol); }
    });

    // populate the hidden columns
    template.columns.forEach((col) => {
      let foundCol = myCols.find(myCol => myCol.key.toLowerCase() === col.key.toLowerCase());
      if (!foundCol) { otherCols.push(col); }
    });

    // load the rules from the quick list (or start anew)
    let query = JSON.parse('{ "id": "q-id", "rules": [], "combinator": "and" }');
    if (quickList.filterCode.startsWith("{")) {
      query = JSON.parse(quickList.filterCode);
    }

    this.setState({
      templateCode: template.templateCode,
      quickList: quickList,
      queryFields: queryFields,
      query: query,
      operators: operators,
      selectedList: myCols,
      unselectedList: otherCols,
      hasChanges: true,
      createMode: createMode
    });
  }

  onChangeTemplate({ value }) {
    let template = this.props.templateList.find((t) => t.templateCode === value);
    if (template) {
      let { quickList } = this.state;
      quickList.templateCode = value;
      quickList.template = template;
      quickList.filterCode = "";
      quickList.columnKeys = "";
      this.loadTemplate(template, quickList, false);
    }
  }



  /**
   * A semi-generic way to handle multiple lists. Matches
   * the IDs of the droppable container to the names of the
   * source arrays stored in the state.
   */
  getList(id) {
    return this.state[id2List[id]];
  }
  setList(id, items) {
    if (id === "unselectedList" || id === "droppable2") { this.setState({ unselectedList: items }); }
    else if (id === "selectedList" || id === "droppable") { this.setState({ selectedList: items }); }
  }

  onChange() {
    this.setState({ hasChanges: true });
  }

  getOperators(fieldName) {
    let foundField = this.state.queryFields.find(f => f.value === fieldName);
    let operators = [];
    if (foundField) {
      foundField.operators.forEach(key => {
        let foundCol = this.state.operators.find(c => c.name === key);
        if (foundCol) { operators.push(foundCol); }
      });
    }
    return operators;
  }

  onValidateRules(quickList) {
    // alert("validating the rules " + JSON.stringify(this.state.query));
    // validate the fields
    let errors = [];
    if (quickList.name === "") {
      errors.push("Name field is required");
    }
    if (quickList.group === "") {
      errors.push("A Group name is required");
    }
    if (this.state.query.rules.length === 0 && quickList.filterRequired) {
      errors.push("Filter Rule(s) are Required");
    }
    if (this.state.quickListNameError)
      errors.push("Quick Lists Maximum Length is 48 Characters");
    if (errors.length) {
      alert(errors.join(".   "));
      return false;
    }
    return true;
  }

  onClone() {
    let quickList = this.getUpdateModel();
    if (this.onValidateRules(quickList)) {
      quickList.id = 0; // this forces a new record
      this.props.onApply(quickList);
    }
  }

  onSave() {
    const { hasChanges, createMode } = this.state;
    if (!hasChanges && !createMode) {
      this.props.onClose();
      return;
    }

    let quickList = this.getUpdateModel();
    if (!this.onValidateRules(quickList)) return;

    if (quickList.id < 0) {
      alert("Changes to a Static Quick List are not permitted.  You can Save a new Quick List.");
      return;
    }
    this.props.onApply(quickList);
  }

  // build an update model based on the QuickListItemViewModel class
  getUpdateModel() {
    const { query, selectedList, templateCode, quickList, name, description, group } = this.state;
    return {
      id: quickList.id,
      quickListCode: quickList.quickListCode,
      name: name.length ? name : quickList.name,
      description: description.length ? description : quickList.description,
      templateCode: templateCode,
      priorityCode: quickList.priorityCode,
      group: group?.value ?? quickList.group,
      sortOrder: quickList.sortOrder,
      filterCode: JSON.stringify(this.fixQuery(query)),
      columnKeys: selectedList.map((c) => c.key),
      filterRequired: quickList.template.filterRequired ?? true,
    };
  }

  fixQuery({ rules, ...otherQueryProperties }) {
    return {
      rules: rules.map(({ field, operator, value, ...otherRuleProperties }) => ({
        field: field.value ? field.value : field,
        operator: typeof operator === "object" && operator !== null ? (
          operator[Object.keys(operator)[0]]
        ) : (
            operator
          ),
        value: value === "" ? (
          2
        ) : (
            value
          ),
        ...otherRuleProperties,
      })),
      ...otherQueryProperties
    };
  }

  onDragEnd(result) {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      // dropped on the same list 
      const items = reorder(
        this.getList(source.droppableId),
        source.index,
        destination.index
      );
      this.setList(source.droppableId, items);
    }
    else {
      // dropped on a different list 
      const result = move(
        this.getList(source.droppableId),
        this.getList(destination.droppableId),
        source,
        destination
      );
      this.setList("selectedList", result.droppable);
      this.setList("unselectedList", result.droppable2);
    }
    this.setState({ hasChanges: true });
  }

  getActions() {
    let actions = [];
    if (this.state.simpleMode) actions.push({ icon: "playlist_add", onClick: this.changeQuickListMode, helpText: `Switch to ${this.state.simpleMode ? "complex" : "simple"} mode` });
    if (this.props.onApply) actions.push({ code: ACTION_APPLY, onClick: this.onSave, helpText: "Save Changes" });
    if (this.props.onClose) actions.push({ code: ACTION_CLOSE, onClick: this.props.onClose, helpText: "Cancel Changes" });
    if (this.props.onApply && !this.state.createMode) actions.push({ code: ACTION_CLONE, onClick: this.onClone, helpText: "Save As New Quick List" });
    return actions;
  }

  quickListEditSelector = () => document.querySelector(".QuickListEdit");
  quickListNameOnChange = value => {
    const isError = value.length > 48;
    if (this.state.quickListNameError !== isError) {
      this.setState({ quickListNameError: isError });
    } else {
      this.setState({ name: value, quickListNameError: false });
    }
  }; 

  getDroppableContent = list => {
    return (provided, snapshot) => (
      <div
        className="react-grid-droppable"
        ref={provided.innerRef}
        style={getListStyle(snapshot.isDraggingOver)}>
        {list?.map((col, index) => (
          <Draggable
            key={col.key}
            draggableId={col.key}
            index={index}>
            {(provided, snapshot) => (
              <QuickListItem
                item={col}
                provided={provided}
                snapshot={snapshot}
              />
            )
            }
          </Draggable>
        ))}
        {provided.placeholder}
      </div>
    );
  };
  getSelectedColumns = () => this.getDroppableContent(this.state.selectedList);
  getUnselectedColumns = () => this.getDroppableContent(this.state.unselectedList);

  onDescriptionChange = value => this.setState({ description: value });
  onGroupChange = value => this.setState({ group: value });

  render() {
    const { templateList, currentGroupNames } = this.props;
    const { activeTemplate, simpleMode } = this.state;
    const item = this.state.quickList;
    let templateSelectList = templateList.map((t) => {
      return { value: t.templateCode, label: t.name };
    });

    return (
      <MDialog
        className={"QuickListEdit"}
        id={"QuickList-" + item.id}
        title={item.name || "Create a New Quick List!"}
        actions={this.getActions()}
        width={simpleMode ? 580 : 800}
        height={700}
      >
        <DataTable plain className="mcard">
          <TableBody>
            <TableRow key="templateCode">
              <TableColumn className="card-field">Base Query</TableColumn>
              <TableColumn>
                <MSelect
                  defaultValue={item.templateCode}
                  options={templateSelectList}
                  onChange={this.onChangeTemplate}
                  getMenuPortalTarget={this.quickListEditSelector}
                  noPadding={true}
                  isClearable={false}
                />
              </TableColumn>
            </TableRow>
            <TableRow key="group">
              <TableColumn className="card-field">Group</TableColumn>
              <TableColumn>
                <MSelect
                  defaultValue={item.group || ""}
                  options={currentGroupNames}
                  creatable={true}
                  getMenuPortalTarget={this.quickListEditSelector}
                  onChange={this.onGroupChange}
                  noPadding={true}
                />
              </TableColumn>
            </TableRow>
            <TableRow key="name">
              <TableColumn className="card-field">Quick List Name</TableColumn>
              <TableColumn>
                <TextField
                  id="name"
                  defaultValue={item.name}
                  onChange={this.quickListNameOnChange}
                />
              </TableColumn>
            </TableRow>
            <TableRow key="description">
              <TableColumn className="card-field">Description</TableColumn>
              <TableColumn>
                <TextField
                  id="description"
                  defaultValue={item.description}
                  onChange={this.onDescriptionChange}
                />
              </TableColumn>
            </TableRow>
          </TableBody>
        </DataTable>
        <DataTable plain className="mcard">
          <TableBody>
            <TableRow key="filter-editor">
              <TableColumn colSpan={2} className="card-field">
                <div style={{ display: "block", height: "30px" }}>
                  Quick List Filters
                </div>
                <div className="mquery">
                  <QueryBuilder
                    key={this.state.templateCode}
                    {...{ activeTemplate }}
                    controlClassnames={controlClassnames}
                    translations={translations}
                    query={this.state.query}
                    fields={this.state.queryFields}
                    operators={this.state.operators}
                    getOperators={this.getOperators}
                    controlElements={controlElements}
                    simpleMode={simpleMode}
                  />
                </div>
                <br />
              </TableColumn>
            </TableRow>
            {!simpleMode && <TableRow key="column-editor">
              <TableColumn colSpan={2} className="card-field">
                Grid Column Selection
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Grid className="quick-list-grid" style={{ marginLeft: 0, paddingLeft: 24 }}>
                    <Cell size={5} style={{ marginLeft: 0, paddingLeft: 0 }} >
                      <label className="md-text--secondary">Selected Columns</label>
                      <Droppable droppableId="droppable">
                        {this.getSelectedColumns()}
                      </Droppable>
                    </Cell>
                    <Cell size={5}>
                      <label className="md-text--secondary">Hidden Columns</label>
                      <Droppable droppableId="droppable2">
                        {this.getUnselectedColumns()}
                      </Droppable>
                    </Cell>
                  </Grid>
                </DragDropContext>
              </TableColumn>
            </TableRow>}
          </TableBody>
        </DataTable>
      </MDialog>
    );
  }
}

QuickListEdit.propTypes = {
  templateList: PropTypes.array.isRequired,
  currentGroupNames: PropTypes.array,
  id: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultToSimpleMode: PropTypes.bool.isRequired,
};


const mapStateToProps = state => {
  return {
    defaultToSimpleMode: userHasRight(SIMPLE_QUICK_LIST_EDIT)(state),
  };
};

export default connect(mapStateToProps)(QuickListEdit);
